import {
  type AudienceFilter,
  TARGETABLE_RANGE_DIMENSION_ID,
  audienceRangeText,
  isRangeFilter,
} from '@sigora/domain';

import type { AudienceCatalog } from '../api/client.ts';

/**
 * One demographic or one choice as the card shows it, and whether the panel disowned it.
 *
 * `unrecognised` is a claim about the catalog, not about the value, which is why it is not simply
 * the inverse of "was labelled". An audience document outlives the catalog it was written against —
 * a demographic can be retired, an option renumbered — and the honest answer to a value the panel
 * no longer lists is the value itself, said quietly. But saying it quietly is only honest when
 * there was a panel to ask: with no catalog, every value would be flagged and the flag would mean
 * nothing at all.
 */
export interface AudienceLabel {
  text: string;
  unrecognised: boolean;
}

export interface LabelledAudienceFilter {
  dimension: AudienceLabel;
  /** The options a select filter targets, or a range filter's one span, `25–44`. */
  options: readonly AudienceLabel[];
  /** Whether `options` is a range's span, which reads after the label with no colon: `Age 25–44`. */
  range: boolean;
}

/**
 * Ids exactly, labels case-insensitively — the same rule `findDimension` and `findOptionValue`
 * apply in `packages/remesh/src/wire.ts` on the way to the panel.
 *
 * That a value already in words matches at all is what the label arm is for: the audience artifact
 * stores ids, and everything downstream of it reads them as ids, but documents written before the
 * agent had a real catalog hold labels in the same field. Mirroring the apply path rather than
 * being merely lenient is the point — anything this card shows as recognised is something the
 * apply call would in fact resolve, so the mute never contradicts what happens at launch.
 */
const findEntry = <Entry extends { id: string; label: string }>(
  raw: string,
  entries: readonly Entry[],
): Entry | undefined =>
  entries.find((entry) => entry.id === raw) ??
  entries.find((entry) => entry.label.toLowerCase() === raw.toLowerCase());

/**
 * A filter's ids resolved against the panel catalog, for display only.
 *
 * An unrecognised demographic takes its options down with it, and deliberately: an option id means
 * nothing except inside its demographic, so matching `"8"` against whatever other demographic
 * happens to have an eighth option would produce a confident, wrong sentence — the one outcome
 * worse than showing the id.
 *
 * A range filter's span is its bounds, which need no catalog to read. It is unrecognised when the
 * apply call would refuse it: when its demographic is unknown, is not the one demographic a range
 * can target, or offers options. A capture-only range recruits whatever the answer, so it shows no
 * span, and its demographic is the part muted when the range could not be applied.
 */
export const labelAudienceFilter = (
  filter: AudienceFilter,
  catalog: AudienceCatalog | null,
): LabelledAudienceFilter => {
  /**
   * No catalog and an unavailable one are the same fact here: nothing was checked. Both render
   * plainly, because a card that muted every value would be reporting a panel that disowns the
   * whole audience — which is the opposite of what happened.
   */
  const dimensions = catalog !== null && catalog.available ? catalog.dimensions : null;
  const checked = dimensions !== null;
  const dimension = dimensions === null ? undefined : findEntry(filter.dimension, dimensions);
  const label = dimension?.label ?? filter.dimension;

  if (isRangeFilter(filter)) {
    const recruitable =
      dimension !== undefined &&
      dimension.id === TARGETABLE_RANGE_DIMENSION_ID &&
      dimension.options.length === 0;
    const unrecognised = checked && !recruitable;

    return filter.mode === 'capture_only'
      ? { dimension: { text: label, unrecognised }, options: [], range: true }
      : {
          dimension: { text: label, unrecognised: checked && dimension === undefined },
          options: [{ text: audienceRangeText(filter.range), unrecognised }],
          range: true,
        };
  }

  if (dimension === undefined) {
    return {
      dimension: { text: label, unrecognised: checked },
      options: filter.selected_options.map((option) => ({ text: option, unrecognised: checked })),
      range: false,
    };
  }

  const options = dimension.options.map((option) => ({ id: option.value, label: option.label }));

  return {
    dimension: { text: dimension.label, unrecognised: false },
    options: filter.selected_options.map((selected) => {
      const option = findEntry(selected, options);

      return option === undefined
        ? { text: selected, unrecognised: true }
        : { text: option.label, unrecognised: false };
    }),
    range: false,
  };
};
